/**
 * Per-step LiveSnapshot efficacy stats.
 *
 * The warm-snapshot reuse + conditional-screenshot path is only worth keeping
 * if reuse hits are frequent and captures stay cheap. None of that was
 * measurable before — this accumulator collects the signals from two layers:
 *
 *   - the executor (`recordExecutorEvent`): reuse hit/miss, before/after capture
 *     timing, and screenshot attach/withhold decisions, and
 *   - the LiveSnapshot manager (`recordManagerEvent`): fresh-augment counts +
 *     wall time, background-warm completions/preemptions, and the serialized
 *     chain wait (`warm-block`).
 *
 * One accumulator lives per browser session; the agent drains it once per step
 * (`drain()`), so each `workflow_run_steps.metadata_json.liveSnapshot` row is a
 * single L3 execution. `drain()` returns null when nothing was recorded, so
 * orgs without the feature (and no-op steps) persist nothing.
 *
 * @module live-snapshot-stats
 */

import type { LiveSnapshotEvent } from './live-snapshot.js';

/** Aggregated LiveSnapshot counters for one step. */
export interface LiveSnapshotStats {
  /** `getReusableSnapshotYaml()` returned a usable warm snapshot. */
  reuseHits: number;
  /** `getReusableSnapshotYaml()` missed → a fresh capture was taken. */
  reuseMisses: number;
  /** Total wall time across before-action captures (reuse check + fresh fallback). */
  beforeCaptureMs: number;
  beforeCaptureCount: number;
  /** Total wall time across after-action captures. */
  afterCaptureMs: number;
  afterCaptureCount: number;
  /** Screenshots actually attached to the agent turn. */
  screenshotsAttached: number;
  /** Screenshots withheld as not visually orienting. */
  screenshotsWithheld: number;
  /** Distribution of screenshot decisions by reason (incl. `"withheld"`). */
  reasonCounts: Record<string, number>;
  /** Background warms that performed a fresh augment during think-time. */
  warmCount: number;
  /** Background warms that yielded to a concurrent real request instead of minting. */
  warmPreemptedCount: number;
  /** Total time real requests waited behind in-flight work on the capture chain. */
  warmBlockMs: number;
  /** Fresh-augment passes driven by a real request (a freshness miss). */
  mintCount: number;
  /** Total `augment()` calls (real + warm + background). */
  augmentCount: number;
  /** Total `augment()` wall time. */
  augmentMs: number;
}

/**
 * Executor-layer events. The executor sees reuse hit/miss and screenshot
 * decisions; it pushes these to the client-owned accumulator (it has no
 * accumulator of its own — see the LiveSnapshot layering notes).
 */
export type LiveSnapshotExecutorEvent =
  | { kind: 'reuse'; hit: boolean }
  | { kind: 'before-capture'; ms: number }
  | { kind: 'after-capture'; ms: number }
  /** `reason` present ⇒ a screenshot was attached; absent ⇒ withheld. */
  | { kind: 'screenshot'; reason?: string };

export function emptyLiveSnapshotStats(): LiveSnapshotStats {
  return {
    reuseHits: 0,
    reuseMisses: 0,
    beforeCaptureMs: 0,
    beforeCaptureCount: 0,
    afterCaptureMs: 0,
    afterCaptureCount: 0,
    screenshotsAttached: 0,
    screenshotsWithheld: 0,
    reasonCounts: {},
    warmCount: 0,
    warmPreemptedCount: 0,
    warmBlockMs: 0,
    mintCount: 0,
    augmentCount: 0,
    augmentMs: 0,
  };
}

/**
 * Accumulates LiveSnapshot telemetry for one step. Mutators are typed so callers
 * never touch fields directly; `recordManagerEvent` / `recordExecutorEvent`
 * dispatch the two event streams onto them.
 */
export class LiveSnapshotStatsAccumulator {
  private s = emptyLiveSnapshotStats();
  private touched = false;

  recordReuse(hit: boolean): void {
    this.touched = true;
    if (hit) this.s.reuseHits += 1;
    else this.s.reuseMisses += 1;
  }

  recordBeforeCapture(ms: number): void {
    this.touched = true;
    this.s.beforeCaptureMs += ms;
    this.s.beforeCaptureCount += 1;
  }

  recordAfterCapture(ms: number): void {
    this.touched = true;
    this.s.afterCaptureMs += ms;
    this.s.afterCaptureCount += 1;
  }

  /** `reason` present ⇒ attached under that reason; absent ⇒ withheld. */
  recordScreenshot(reason?: string): void {
    this.touched = true;
    if (reason) {
      this.s.screenshotsAttached += 1;
      this.s.reasonCounts[reason] = (this.s.reasonCounts[reason] ?? 0) + 1;
    } else {
      this.s.screenshotsWithheld += 1;
      this.s.reasonCounts.withheld = (this.s.reasonCounts.withheld ?? 0) + 1;
    }
  }

  recordWarm(): void {
    this.touched = true;
    this.s.warmCount += 1;
  }

  recordWarmPreempted(): void {
    this.touched = true;
    this.s.warmPreemptedCount += 1;
  }

  recordWarmBlock(ms: number): void {
    this.touched = true;
    this.s.warmBlockMs += ms;
  }

  recordMint(): void {
    this.touched = true;
    this.s.mintCount += 1;
  }

  recordAugment(ms: number): void {
    this.touched = true;
    this.s.augmentCount += 1;
    this.s.augmentMs += ms;
  }

  /** Dispatch a manager-layer event onto the counters. */
  recordManagerEvent(ev: LiveSnapshotEvent): void {
    switch (ev.kind) {
      case 'mint':
        this.recordMint();
        break;
      case 'augment':
        this.recordAugment(ev.ms);
        break;
      case 'warm':
        this.recordWarm();
        break;
      case 'warm-preempted':
        this.recordWarmPreempted();
        break;
      case 'warm-block':
        this.recordWarmBlock(ev.ms);
        break;
    }
  }

  /** Dispatch an executor-layer event onto the counters. */
  recordExecutorEvent(ev: LiveSnapshotExecutorEvent): void {
    switch (ev.kind) {
      case 'reuse':
        this.recordReuse(ev.hit);
        break;
      case 'before-capture':
        this.recordBeforeCapture(ev.ms);
        break;
      case 'after-capture':
        this.recordAfterCapture(ev.ms);
        break;
      case 'screenshot':
        this.recordScreenshot(ev.reason);
        break;
    }
  }

  /** Current totals without resetting. */
  peek(): LiveSnapshotStats {
    return this.s;
  }

  /**
   * Snapshot the current totals and reset to empty. Returns null when nothing
   * was recorded since the last drain, so off-feature steps persist nothing.
   */
  drain(): LiveSnapshotStats | null {
    if (!this.touched) return null;
    const out = this.s;
    this.s = emptyLiveSnapshotStats();
    this.touched = false;
    return out;
  }
}
